import DefaultAudioPlayer from "components/UI/mediaPlayers/defaultAudioPlayer";
import DefaultVideoPlayer from "components/UI/mediaPlayers/defaultVideoPlayer";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal } from "antd";
import { useCurrentClass } from "store/classes/selectors";
import { useCurrentUser } from "store/users/selectors";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import * as Sentry from '@sentry/react';

const CanvasContainer = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  cursor: ${({ isPointer }) => (isPointer ? "pointer" : "default")};
  width: 100%;
  height: 100%;
  touch-action: none; // Prevents default touch behaviors
`;


const Image = styled.img`
  background-color: ${COLOR.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23);
  height: auto;
  width: 100%;
`;

const Container = styled.div`
  margin-top: 27px;
  position: relative;
  margin-bottom: 30px;
`;

const SlideImageHighlighterWidget = ({
  imageUrl,
  pageId,
  bookId,
  setImageConfiguration,
  getBookSlides,
  activeSlide,
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  
  // State variables
  const [rectangles, setRectangles] = useState([]);
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 1,
    height: 1,
  });
  const [isAnnotationsVisible, setIsAnnotationsVisible] = useState(false);
  const [hoveredRectIndex, setHoveredRectIndex] = useState(null);
  const [highlights, setHighlights] = useState({});
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isPointer, setIsPointer] = useState(false);
  const [openVideoModal, setVideoModal] = useState(false);
  const [openAudioPlayer, setOpenAudioPlayer] = useState(false);
  const [openDefinitionModal, setOpenDefinitionModal] = useState(false);
  const [definitionText, setDefinitionText] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  
  // Key ref to track active slide changes
  const prevActiveSlideRef = useRef(activeSlide);

  // Redraw canvas with current highlights
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) return;
    
    try {
      // Update canvas dimensions to match current image
      canvas.width = image.clientWidth;
      canvas.height = image.clientHeight;
      
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!rectangles?.length) return;
      
      const currentNaturalDimensions = rectangles[0]?.naturalDimensions || naturalDimensions;
      
      rectangles.forEach((rect) => {
        if (rect.isHighlighterMode) {
          const opacity = rect.opacity || 0.4;
          const hexOpacity = Math.floor(opacity * 255).toString(16).padStart(2, '0');
          ctx.fillStyle = `${rect.color}${hexOpacity}`;
          
          ctx.fillRect(
            (rect.x / currentNaturalDimensions.width) * canvas.width,
            (rect.y / currentNaturalDimensions.height) * canvas.height,
            (rect.width / currentNaturalDimensions.width) * canvas.width,
            (rect.height / currentNaturalDimensions.height) * canvas.height
          );
        } else {
          ctx.strokeStyle = rect.color;
          ctx.lineWidth = rect.strokeWidth || 2;
          
          ctx.strokeRect(
            (rect.x / currentNaturalDimensions.width) * canvas.width,
            (rect.y / currentNaturalDimensions.height) * canvas.height,
            (rect.width / currentNaturalDimensions.width) * canvas.width,
            (rect.height / currentNaturalDimensions.height) * canvas.height
          );
        }
      });
      
    } catch (error) {
      Sentry.captureException(`635 Error in redraw canvas: ${error}`);
      console.error("Error in redraw canvas:", error);
    }
  }, [rectangles, naturalDimensions]);


  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const image = imageRef.current;
      
      if (!canvas || !image) return;
      
      canvas.width = image.clientWidth;
      canvas.height = image.clientHeight;
      
      setImageConfiguration({
        width: image.naturalWidth,
        height: image.naturalHeight,
        clientWidth: image.clientWidth,
        clientHeight: image.clientHeight,
      });
      
      redrawCanvas();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [redrawCanvas, setImageConfiguration]);

  // Load natural dimensions when image loads
  const handleImageLoad = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) return;
    
    setIsImageLoaded(true);
    
    const newNaturalDimensions = {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
    
    setNaturalDimensions(newNaturalDimensions);
    setImageConfiguration({
      width: image.naturalWidth,
      height: image.naturalHeight,
      clientWidth: image.clientWidth,
      clientHeight: image.clientHeight,
    });
    
    canvas.width = image.clientWidth;
    canvas.height = image.clientHeight;
    
    redrawCanvas();
    
  }, [redrawCanvas, setImageConfiguration]);

  useEffect(() => {

    if (prevActiveSlideRef.current !== activeSlide) {      
      const slideData = getBookSlides?.pages?.[activeSlide]?.data;
      
      if (Array.isArray(slideData) && slideData.length > 0) {
        const processedRectangles = slideData.map(rect => ({
          ...rect,
          color: "transparent",
          isHighlighterMode: false
        }));
  
        setRectangles(processedRectangles);
        
        if (processedRectangles[0]?.naturalDimensions) {
          setNaturalDimensions(processedRectangles[0].naturalDimensions);
        }
  
        setTimeout(() => {
          redrawCanvas();
        }, 0);
      } else {
        setRectangles([]);
      }
    }
    
    prevActiveSlideRef.current = activeSlide;
  }, [activeSlide, getBookSlides, redrawCanvas]);


  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        if (!bookId || !pageId || !getBookSlides) return;
        
        if (
          highlights &&
          highlights.page_id === getBookSlides.page_id &&
          highlights.book_id === getBookSlides.book_id
        ) {
          return;
        }
        
        setHighlights(getBookSlides);
        
        const slideData = getBookSlides?.pages?.[activeSlide]?.data;
        
        if (Array.isArray(slideData) && slideData.length > 0) {
          const processedRectangles = slideData.map(rect => ({
            ...rect,
            color: "transparent",
            isHighlighterMode: false
          }));
          
          setRectangles(processedRectangles);
          if (processedRectangles[0]?.naturalDimensions) {
            setNaturalDimensions(processedRectangles[0].naturalDimensions);
          }
        } else {
          setRectangles([]);
          setHighlights({
            page_id: pageId,
            book_id: bookId,
            pages: [],
          });
        }
      } catch (error) {
        Sentry.captureException(`636 Error in fetchHighlights: ${error}`);
        console.error("Error fetching highlights:", error);
        setRectangles([]);
      }
    };

    fetchHighlights();
  }, [bookId, pageId, getBookSlides, activeSlide]);

  // Redraw canvas when relevant state changes
  useEffect(() => {
    if (isImageLoaded) {
      redrawCanvas();
    }
  }, [redrawCanvas, isImageLoaded, rectangles, isAnnotationsVisible, hoveredRectIndex,activeSlide]);


const getRelativePosition = useCallback((event) => {
    try {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        
        const rect = canvas.getBoundingClientRect();
        const currentNaturalDimensions = rectangles[0]?.naturalDimensions || naturalDimensions;
        
        // Handle both touch and mouse events
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        
        return {
            x: ((clientX - rect.left) / rect.width) * currentNaturalDimensions.width,
            y: ((clientY - rect.top) / rect.height) * currentNaturalDimensions.height,
        };
    } catch (error) {
      Sentry.captureException(`637 Error in getRelativePosition: ${error}`);
        console.error("Error in positioning:", error);
        return { x: 0, y: 0 };
    }
}, [naturalDimensions, rectangles]);


  const checkContainerIndex = useCallback((x, y) => {
    return rectangles.findIndex((rect) => {
      const rectX1 = Math.min(rect.x, rect.x + rect.width);
      const rectX2 = Math.max(rect.x, rect.x + rect.width);
      const rectY1 = Math.min(rect.y, rect.y + rect.height);
      const rectY2 = Math.max(rect.y, rect.y + rect.height);
      
      return x >= rectX1 && x <= rectX2 && y >= rectY1 && y <= rectY2;
    });
  }, [rectangles]);


  const listClickedItem = useCallback((x, y) => {
    return rectangles.filter((rect) => {
      const rectX1 = Math.min(rect.x, rect.x + rect.width);
      const rectX2 = Math.max(rect.x, rect.x + rect.width);
      const rectY1 = Math.min(rect.y, rect.y + rect.height);
      const rectY2 = Math.max(rect.y, rect.y + rect.height);
      
      return x >= rectX1 && x <= rectX2 && y >= rectY1 && y <= rectY2;
    });
  }, [rectangles]);


  const getEventCoordinates = useCallback((e) => {
    if (e.type === "touchstart" || e.type === "touchend") {
      const touch = e.changedTouches[0];
      return {
        clientX: touch.clientX,
        clientY: touch.clientY,
      };
    }
    return {
      clientX: e.clientX,
      clientY: e.clientY,
    };
  }, []);


  const calculateCanvasCoordinates = useCallback((clientX, clientY, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    const scaleX = canvas.width / canvas.clientWidth;
    const scaleY = canvas.height / canvas.clientHeight;

    return {
      scaledX: mouseX * scaleX,
      scaledY: mouseY * scaleY,
    };
  }, []);

 
  const isClickInside = useCallback((x, y, button) => {
    return (
      x >= button.x &&
      x <= button.x + button.width &&
      y >= button.y &&
      y <= button.y + button.height
    );
  }, []);


  const handleRectangleClick = useCallback((rectangle) => {
    if (rectangle?.audioFile) {
      setOpenAudioPlayer(true);
      setAudioUrl(rectangle.audioFile);
    } else if (rectangle?.videoFile) {
      setVideoModal(true);
      setVideoUrl(rectangle.videoFile);
    } else if (rectangle?.definition) {
      setDefinitionText(rectangle.definition);
      setOpenDefinitionModal(true);
    }
  }, []);


 const handleCanvasClick = useCallback((e) => {
     e.preventDefault();
     
     // Get the correct event object (touch or mouse)
     const event = e.touches ? e.touches[0] : e;
     
     const canvas = canvasRef.current;
     if (!canvas) return;
 
     const { x, y } = getRelativePosition(event);
     const itemIndex = checkContainerIndex(x, y);
     
     if (itemIndex < 0) return;
     
     const rectItem = rectangles[itemIndex];
     if (!rectItem) return;
     
     const { clientX, clientY } = getEventCoordinates(e);
     const { scaledX, scaledY } = calculateCanvasCoordinates(
       clientX,
       clientY,
       canvas
     );
 
     if (rectItem.crossButton && isClickInside(scaledX, scaledY, rectItem.crossButton)) {
       return;
     }
     
     handleRectangleClick(rectItem);
 }, [
     getRelativePosition, 
     checkContainerIndex, 
     rectangles, 
     getEventCoordinates, 
     calculateCanvasCoordinates, 
     isClickInside, 
     handleRectangleClick
 ]);
 

  // Handle modal close
  const handleClose = useCallback((type) => {
    if (type === "video") {
      setVideoModal(false);
    } else if (type === "definition") {
      setOpenDefinitionModal(false);
      setDefinitionText("");
    } else {
      setOpenAudioPlayer(false);
    }
  }, []);


 const handleMouseMove = useCallback((e) => {
     // Handle both touch and mouse events
     const event = e.touches ? e.touches[0] : e;
     const { x, y } = getRelativePosition(event);
   
     const hoveredIndex = rectangles.findIndex((rect) => {
         const rectX1 = Math.min(rect.x, rect.x + rect.width);
         const rectX2 = Math.max(rect.x, rect.x + rect.width);
         const rectY1 = Math.min(rect.y, rect.y + rect.height);
         const rectY2 = Math.max(rect.y, rect.y + rect.height);
     
         return x >= rectX1 && x <= rectX2 && y >= rectY1 && y <= rectY2;
     });
 
     setIsPointer(hoveredIndex !== -1);
     setHoveredRectIndex(hoveredIndex !== -1 ? hoveredIndex : null);
 
     if (hoveredIndex === -1) {
         setIsPointer(false);
         setHoveredRectIndex(null);
     }
 }, [getRelativePosition, rectangles]);
 

  return (
    <>
      <DefaultVideoPlayer
        openVideoModal={openVideoModal}
        handleClose={() => handleClose("video")}
        video={videoUrl}
        from="slideWidget"
      />
      <DefaultAudioPlayer
        openAudioPlayer={openAudioPlayer}
        handleClose={() => handleClose("audio")}
        audio={audioUrl}
        from="slideWidget"
      />
      <Modal
        title="Definition"
        open={openDefinitionModal}
        onCancel={() => handleClose("definition")}
        footer={null}
      >
      <div style={{ maxHeight: '150px', overflowY: 'auto'}}>
        <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{definitionText}</p>
      </div>
      </Modal>
      <Container style={{ height: "100%" }} className="container to_be_print">
        <Image
          ref={imageRef}
          src={imageUrl?.replace("/public", "/original")}
          alt="To be highlighted"
          className="image to_be_print"
          style={{
            height: "100%",
            background: "none",
            boxShadow: "none",
            objectFit: "contain",
          }}
          onLoad={handleImageLoad}
        />
      <CanvasContainer
          ref={canvasRef}
          isPointer={isPointer}
          className="highlight-canvas"
          onClick={handleCanvasClick}
          onTouchStart={handleCanvasClick}
          onMouseLeave={() => setHoveredRectIndex(null)}
          onMouseMove={handleMouseMove}
          onTouchMove={handleMouseMove}
      />
      </Container>
    </>
  );
};

export default SlideImageHighlighterWidget;